import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiError, ApiResponse } from '../types/api';
import {
    ProjectAddableKind,
    ProjectDataSourceEntry,
    ProjectDataSourceKind,
} from '../types/projectDataSource';

export const projectDataSourcesKey = (projectId: string | number | undefined) => [
    'project',
    projectId !== undefined ? String(projectId) : undefined,
    'data-sources',
];

/** The sources a project was built from, with last-refreshed state. Polls
 *  while any refresh is in flight so progress and completion show live. */
export const useProjectDataSources = (projectId: string | number | undefined) => {
    return useQuery({
        queryKey: projectDataSourcesKey(projectId),
        queryFn: () =>
            api.request<ApiResponse<ProjectDataSourceEntry[]>>(
                `/api/projects/${projectId}/data-sources`
            ),
        enabled: projectId !== undefined && projectId !== '',
        refetchOnWindowFocus: false,
        refetchInterval: (query) => {
            const entries = query.state.data?.data;
            return entries?.some((e) => e.status === 'refreshing') ? 3000 : false;
        },
    });
};

export const useRefreshProjectDataSource = (projectId: string | number | undefined) => {
    const queryClient = useQueryClient();
    return useMutation<
        ApiResponse<ProjectDataSourceEntry>,
        ApiError,
        { sourceId: number; kind: ProjectDataSourceKind }
    >({
        // `kind` disambiguates a source attached both as treatments and roads.
        mutationFn: ({ sourceId, kind }) =>
            api.request<ApiResponse<ProjectDataSourceEntry>>(
                `/api/projects/${projectId}/data-sources/${sourceId}/refresh`,
                { method: 'POST', body: { kind } }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectDataSourcesKey(projectId) });
            // The project flips to status "refreshing"; its query polls until done.
            queryClient.invalidateQueries({
                queryKey: ['project', projectId !== undefined ? String(projectId) : undefined],
            });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
};

/** Attach a source to an existing project under `kind` (treatments, assets,
 *  fire history, or roads stamped into vegetation). The backend loads it as a
 *  refresh of the new entry, so the same polling row shows its progress. */
export const useAddProjectDataSource = (projectId: string | number | undefined) => {
    const queryClient = useQueryClient();
    return useMutation<
        ApiResponse<ProjectDataSourceEntry>,
        ApiError,
        { kind: ProjectAddableKind; sourceId: number }
    >({
        mutationFn: ({ kind, sourceId }) =>
            api.request<ApiResponse<ProjectDataSourceEntry>>(
                `/api/projects/${projectId}/data-sources`,
                { method: 'POST', body: { kind, source_id: sourceId } }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectDataSourcesKey(projectId) });
            queryClient.invalidateQueries({
                queryKey: ['project', projectId !== undefined ? String(projectId) : undefined],
            });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
};
